import React, { useEffect, useState, Component } from 'react';
import { X, CheckCircle2, Mail } from 'lucide-react';
import confetti from 'canvas-confetti';
import { Button } from './ui/Button';
const SUPPORT_EMAIL = 'support@zanoplatform.com';
// Web3Forms delivers submissions straight to your inbox — no email client.
// Get a free access key at https://web3forms.com and set its destination to
// support@zanoplatform.com, then paste the key below.
const WEB3FORMS_ACCESS_KEY = 'daa0e880-90c5-42b7-911c-ac1a7e0e4820';
export const MarketplaceModal = ({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Reset back to the form whenever the modal is fully closed.
  useEffect(() => {
    if (!isOpen) {
      const t = window.setTimeout(() => {
        setSubmitted(false);
        setError(null);
        setSubmitting(false);
      }, 250);
      return () => window.clearTimeout(t);
    }
  }, [isOpen]);
  if (!isOpen) return null;
  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-zano-pink focus:border-zano-pink outline-none text-sm';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';
  const rainConfetti = () => {
    const duration = 2500;
    const end = Date.now() + duration;
    const colors = ['#EC4899', '#F9A8D4', '#C4B5FD', '#38BDF8', '#FBCFE8'];
    confetti({
      particleCount: 120,
      spread: 90,
      origin: {
        y: 0.3
      },
      colors
    });
    const frame = () => {
      confetti({
        particleCount: 4,
        angle: 90,
        spread: 70,
        startVelocity: 35,
        origin: {
          x: Math.random(),
          y: -0.1
        },
        gravity: 0.8,
        colors
      });
      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };
    frame();
  };
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    const data = new FormData(e.currentTarget);
    const get = (k: string) => data.get(k) as string || '—';
    // Send the submission straight to support@zanoplatform.com via Web3Forms.
    const message = ['ABOUT THE USER', `Full name: ${get('fullName')}`, `Work email: ${get('email')}`, `LinkedIn profile URL: ${get('linkedin')}`, `Job title: ${get('jobTitle')}`, `Country: ${get('country')}`, '', 'COMPANY DETAILS', `Company name: ${get('companyName')}`, `Company website: ${get('website')}`, `Business type: ${get('businessType')}`, '', 'CAMPAIGN DETAILS', `Influencer campaigns per month: ${get('campaignsPerMonth')}`, `Estimated monthly budget: ${get('monthlyBudget')}`, `Type of talent looking for: ${get('talentType')}`, '', 'OTHER INFO', `How did you hear about Zano: ${get('howDidYouHear')}`, `Referred by a Zano user?: ${get('referredBy')}`, `Anything else?: ${get('notes')}`].join('\n');
    const payload = {
      access_key: WEB3FORMS_ACCESS_KEY,
      subject: 'New Marketplace Access Request',
      from_name: 'Zano — Request Marketplace Access form',
      replyto: get('email'),
      message
    };
    try {
      setSubmitting(true);
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const result = await res.json();
      if (!res.ok || !result.success) {
        throw new Error(result.message || 'Submission failed');
      }
      rainConfetti();
      setSubmitted(true);
    } catch (err) {
      setError('Something went wrong sending your request. Please try again or email us directly.');
    } finally {
      setSubmitting(false);
    }
  };
  return <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm" onClick={onClose} data-id="element-804">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl overflow-hidden relative max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()} data-id="element-805">
        <button onClick={onClose} aria-label="Close" className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10" data-id="element-806">
          <X size={20} data-id="element-807" />
        </button>

        {submitted ? <div className="p-8 sm:p-10 text-center" data-id="element-808">
            <div className="mx-auto mb-5 flex items-center justify-center w-16 h-16 rounded-full bg-emerald-50" data-id="element-809">
              <CheckCircle2 size={32} className="text-emerald-500" data-id="element-810" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-811">
              Request received!
            </h3>
            <p className="text-gray-600 text-sm leading-relaxed mb-6" data-id="element-812">
              Thank you, we received your request. Our team will get back to you
              within 48 hours. If you are approved, you will receive an
              invitation to create an account as a marketplace user and
              subscribe to a free trial on the marketplace plan. Any questions,
              email us at{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="text-zano-pink hover:underline" data-id="element-813">
                {SUPPORT_EMAIL}
              </a>
              .
            </p>
            <Button variant="primary" className="w-full sm:w-auto justify-center px-8" onClick={onClose} data-id="element-814">
              Done
            </Button>
          </div> : <div className="p-6 sm:p-8 overflow-y-auto" data-id="element-815">
            <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-816">
              Request Marketplace Access
            </h3>
            <p className="text-gray-600 mb-8 text-sm leading-relaxed" data-id="element-817">
              Get access to talent agency rosters, represented influencer
              profiles, marketplace benchmarks, average pricing insights, and
              the Zano Dashboard. Complete the form below and our team will
              review your request.
            </p>

            <form className="space-y-6" onSubmit={handleSubmit} data-id="element-818">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-819">
                <div data-id="element-820">
                  <label className={labelClass} data-id="element-821">Full name</label>
                  <input name="fullName" type="text" required className={inputClass} data-id="element-822" />
                </div>
                <div data-id="element-823">
                  <label className={labelClass} data-id="element-824">Work email</label>
                  <input name="email" type="email" required className={inputClass} data-id="element-825" />
                </div>
                <div data-id="element-826">
                  <label className={labelClass} data-id="element-827">LinkedIn profile URL</label>
                  <input name="linkedin" type="url" required className={inputClass} data-id="element-828" />
                </div>
                <div data-id="element-829">
                  <label className={labelClass} data-id="element-830">Job title</label>
                  <input name="jobTitle" type="text" required className={inputClass} data-id="element-831" />
                </div>
                <div data-id="element-832">
                  <label className={labelClass} data-id="element-833">Country</label>
                  <input name="country" type="text" required className={inputClass} data-id="element-834" />
                </div>
                <div data-id="element-835">
                  <label className={labelClass} data-id="element-836">Company name</label>
                  <input name="companyName" type="text" required className={inputClass} data-id="element-837" />
                </div>
                <div data-id="element-838">
                  <label className={labelClass} data-id="element-839">Company website</label>
                  <input name="website" type="url" required className={inputClass} data-id="element-840" />
                </div>
                <div data-id="element-841">
                  <label className={labelClass} data-id="element-842">
                    What kind of business are you?
                  </label>
                  <select name="businessType" required className={inputClass} data-id="element-843">
                    <option value="" disabled selected data-id="element-844">
                      Select an option
                    </option>
                    <option data-id="element-845">Brand</option>
                    <option data-id="element-846">Marketing Agency</option>
                    <option data-id="element-847">Talent Agency</option>
                  </select>
                </div>
                <div data-id="element-848">
                  <label className={labelClass} data-id="element-849">
                    How many influencer campaigns do you run each month?
                  </label>
                  <input name="campaignsPerMonth" type="text" required className={inputClass} data-id="element-850" />
                </div>
                <div data-id="element-851">
                  <label className={labelClass} data-id="element-852">
                    Estimated monthly budget for influencer marketing
                  </label>
                  <input name="monthlyBudget" type="text" required className={inputClass} data-id="element-853" />
                </div>
                <div className="sm:col-span-2" data-id="element-854">
                  <label className={labelClass} data-id="element-855">
                    What kind of talent are you looking to work with?
                  </label>
                  <input name="talentType" type="text" placeholder="e.g. influencers, celebrities, public figures, athletes, B2B LinkedIn" required className={inputClass} data-id="element-856" />
                </div>
                <div className="sm:col-span-2" data-id="element-857">
                  <label className={labelClass} data-id="element-858">
                    How did you hear about Zano?
                  </label>
                  <select name="howDidYouHear" required className={inputClass} data-id="element-859">
                    <option value="" disabled selected data-id="element-860">
                      Select an option
                    </option>
                    <option data-id="element-861">LinkedIn</option>
                    <option data-id="element-862">YouTube</option>
                    <option data-id="element-863">Google</option>
                    <option data-id="element-864">Word of mouth</option>
                    <option data-id="element-865">WIIM</option>
                    <option data-id="element-866">ChatGPT</option>
                    <option data-id="element-867">Referral from existing user</option>
                    <option data-id="element-868">Influencer Marketing Hub</option>
                    <option data-id="element-869">Cannes Lions 2026</option>
                    <option data-id="element-870">Product Hunt</option>
                    <option data-id="element-871">Email outreach</option>
                    <option data-id="element-872">The Creator Network</option>
                    <option data-id="element-873">Other</option>
                  </select>
                </div>
                <div className="sm:col-span-2" data-id="element-874">
                  <label className={labelClass} data-id="element-875">
                    Were you referred by a Zano user? If yes, who?
                  </label>
                  <input name="referredBy" type="text" className={inputClass} data-id="element-876" />
                </div>
                <div className="sm:col-span-2" data-id="element-877">
                  <label className={labelClass} data-id="element-878">
                    Anything else you would like us to know?
                  </label>
                  <textarea name="notes" rows={3} className={inputClass} data-id="element-879" />
                </div>
              </div>

              <div className="flex items-start gap-3 pt-2" data-id="element-880">
                <input type="checkbox" id="marketplace-tos" name="tos" required className="mt-1 shrink-0 rounded border-gray-300 text-zano-pink focus:ring-zano-pink" data-id="element-881" />
                <label htmlFor="marketplace-tos" className="text-[11px] text-gray-500 leading-relaxed" data-id="element-882">
                  By checking this box, I acknowledge and agree to the Terms of
                  Service and Privacy Policy by Zano Platform. I acknowledge
                  that Zano Platform may collect, process, and store my personal
                  data provided in this form for the purpose of evaluating my
                  request and providing access to the platform. I understand
                  that my data will be handled in accordance with the Zano
                  Privacy Policy and GDPR regulations.
                </label>
              </div>

              {error && <p className="text-sm text-red-600" role="alert" data-id="element-883">
                  {error}
                </p>}
              <Button variant="primary" className="w-full justify-center py-3 disabled:opacity-60 disabled:cursor-not-allowed" type="submit" disabled={submitting} data-id="element-884">
                {submitting ? 'Sending…' : 'Submit request'}
              </Button>
            </form>
          </div>}
      </div>
    </div>;
};